<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { flushQueue, queueState, refreshQueue, discard } from '@/lib/queue';

/**
 * "3 pending submissions" — visible from anywhere in the app, because a queued
 * registration that nobody knows about is a lost registration (§8).
 */
const expanded = ref(false);
const online = ref(navigator.onLine);
const count = computed(() => queueState.pending.length);

onMounted(() => {
  void refreshQueue();
  window.addEventListener('online', () => (online.value = true));
  window.addEventListener('offline', () => (online.value = false));
});
</script>

<template>
  <div v-if="count" class="border-b border-amber-300 bg-amber-100 text-amber-900">
    <div class="mx-auto flex max-w-6xl flex-wrap items-center gap-3 px-4 py-2 text-sm">
      <strong>{{ count }} pending submission{{ count === 1 ? '' : 's' }}</strong>
      <span v-if="queueState.flushing">sending…</span>
      <span v-else-if="!online">waiting for a connection</span>
      <button class="rounded border border-amber-400 px-2 py-0.5 hover:bg-amber-200" @click="flushQueue">
        Send now
      </button>
      <button class="rounded border border-amber-400 px-2 py-0.5 hover:bg-amber-200" @click="expanded = !expanded">
        {{ expanded ? 'Hide' : 'Details' }}
      </button>
    </div>
    <ul v-if="expanded" class="mx-auto max-w-6xl space-y-1 px-4 pb-3 text-xs">
      <li
        v-for="record in queueState.pending"
        :key="record.id"
        class="flex flex-wrap items-center gap-2 rounded bg-white/70 px-2 py-1"
      >
        <span class="font-semibold">{{ record.label }}</span>
        <span class="font-mono">{{ record.id.slice(0, 8) }}</span>
        <span>attempts: {{ record.attempts }}</span>
        <span v-if="record.progress.school_id">ID {{ record.progress.school_id }}</span>
        <span v-if="record.last_error" class="text-red-700">{{ record.last_error }}</span>
        <button class="ml-auto rounded border px-2 py-0.5 hover:bg-amber-50" @click="discard(record.id)">
          Discard
        </button>
      </li>
    </ul>
  </div>
</template>
